import { scrypt } from 'scrypt-js';
import * as nacl from 'tweetnacl';
import { hexToU8a, u8aToHex } from '../helpers';
import { InMemorySession } from '../helpers/in_memory_session';
export class TrustedKeyStoreService {
    STORAGE_KEY = 'trusted';
    STORAGE_KEY_AUTO_FLAG = 'autoSession';
    SCRYPT_PARAMETERS = {
        NBenchmark: 4096,
        NMin: 65536,
        NTargetMs: 500,
        r: 8,
        p: 1,
    };
    log;
    storage = null;
    blocked = false;
    inMemorySession = new InMemorySession();
    static $inject = ['$window', 'LogService'];
    constructor($window, logService) {
        this.log = logService.getLogger('TrustedKeyStore-S', 'color: #fff; background-color: #666699');
        try {
            if ($window.localStorage === null) {
                this.blocked = true;
            }
            this.storage = $window.localStorage;
        }
        catch (e) {
            this.log.warn('LocalStorage blocked:', e);
            this.blocked = true;
        }
    }
    deprecatedPwToKey(password) {
        const hash = nacl.hash(password);
        return hash.slice(0, nacl.secretbox.keyLength);
    }
    async storeTrustedKey(ownSecretKey, peerPublicKey, pushToken, pushTokenType, password, isAutoSession) {
        const salt = nacl.randomBytes(32);
        const r = this.SCRYPT_PARAMETERS.r;
        const p = this.SCRYPT_PARAMETERS.p;
        let N = this.SCRYPT_PARAMETERS.NBenchmark;
        const benchmarkPassword = nacl.randomBytes(32);
        let start = performance.now();
        await scrypt(benchmarkPassword, salt, N, r, p, nacl.secretbox.keyLength);
        let end = performance.now();
        const extrapolatedN = Math.round((N / (end - start)) * this.SCRYPT_PARAMETERS.NTargetMs);
        N = Math.max(this.SCRYPT_PARAMETERS.NMin, 1 << (31 - Math.clz32(extrapolatedN)));
        if (!Number.isFinite(N)) {
            throw new Error(`Unable to determine scrypt work factor`);
        }
        this.log.debug(`scrypt benchmark determined work factor N=${N} (from ${extrapolatedN}), took ${(end - start).toFixed(0)}ms`);
        const encodedPassword = (new TextEncoder()).encode(password);
        start = performance.now();
        const key = await scrypt(encodedPassword, salt, N, r, p, nacl.secretbox.keyLength);
        end = performance.now();
        this.log.debug(`Used scrypt parameters (N=${N}, r=${r}, p=${p}) for key derivation, took ${(end - start).toFixed(0)}ms`);
        const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
        let encodedToken = new Uint8Array(0);
        if (pushToken !== null && pushTokenType !== null) {
            encodedToken = this.encodePushToken(pushToken, pushTokenType);
        }
        const data = new Uint8Array(2 * 32 + encodedToken.byteLength);
        data.set(ownSecretKey, 0);
        data.set(peerPublicKey, 32);
        data.set(encodedToken, 64);
        const encrypted = nacl.secretbox(data, nonce, key);
        this.log.debug(isAutoSession ? 'Storing trusted key (auto session)' : 'Storing trusted key');
        this.storage.setItem(this.STORAGE_KEY, JSON.stringify({
            nonce: u8aToHex(nonce),
            salt: u8aToHex(salt),
            N: N,
            r: r,
            p: p,
            encrypted: u8aToHex(encrypted),
        }));
        if (isAutoSession) {
            this.storage.setItem(this.STORAGE_KEY_AUTO_FLAG, 'auto');
        }
    }
    hasTrustedKey() {
        const item = this.storage.getItem(this.STORAGE_KEY);
        return item !== null && item.length > 96 && item.indexOf(':') !== -1;
    }
    isAutoSession() {
        return this.storage.getItem(this.STORAGE_KEY_AUTO_FLAG) !== null;
    }
    encodePushToken(token, type) {
        let prefix;
        switch (type) {
            case "gcm":
                prefix = "g";
                break;
            case "apns":
                prefix = "a";
                break;
            case "hms":
                prefix = "h";
                break;
            case "threema-gateway":
                prefix = "3";
                break;
            default:
                throw new Error(`Invalid push token type: ${type}`);
        }
        return (new TextEncoder()).encode(`${prefix}:${token}`);
    }
    decodePushToken(encodedBytes) {
        const encodedString = encodedBytes.byteLength > 0
            ? (new TextDecoder('utf-8', { fatal: true })).decode(encodedBytes)
            : null;
        let type = null;
        let token = null;
        if (encodedString !== null && encodedString[1] === ':') {
            switch (encodedString[0]) {
                case "g":
                    type = "gcm";
                    break;
                case "a":
                    type = "apns";
                    break;
                case "h":
                    type = "hms";
                    break;
                case "3":
                    type = "threema-gateway";
                    break;
                default:
                    this.log.error('Invalid push token type:', encodedString[0]);
                    return { token: null, type: null };
            }
            token = encodedString.slice(2);
        }
        else if (encodedString !== null) {
            token = encodedString;
            type = "gcm";
        }
        return { token: token, type: type };
    }
    async migrateDeprecatedStorageFormat(parts, password) {
        const nonce = hexToU8a(parts[0]);
        const encrypted = hexToU8a(parts[1]);
        const decrypted = nacl.secretbox.open(encrypted, nonce, this.deprecatedPwToKey((new TextEncoder()).encode(password)));
        if (!decrypted) {
            throw new Error('Unable to decrypt deprecated storage');
        }
        const token = this.decodePushToken(decrypted.slice(96));
        const ownSecretKey = decrypted.slice(32, 64);
        const peerPublicKey = decrypted.slice(64, 96);
        this.log.warn('Migrating deprecated key storage to scrypt-based key storage');
        await this.storeTrustedKey(ownSecretKey, peerPublicKey, token.token, token.type, password, this.isAutoSession());
    }
    async retrieveTrustedKey(password) {
        let storedValue = this.storage.getItem(this.STORAGE_KEY);
        if (storedValue === null) {
            return null;
        }
        const parts = storedValue.split(':');
        if (parts.length === 2) {
            try {
                await this.migrateDeprecatedStorageFormat(parts, password);
            }
            catch (error) {
                this.log.error(error);
                return null;
            }
            storedValue = this.storage.getItem(this.STORAGE_KEY);
            if (storedValue === null) {
                return null;
            }
        }
        let raw;
        try {
            raw = JSON.parse(storedValue);
        }
        catch (error) {
            this.log.error(`Invalid storage data`, error);
            return null;
        }
        if (typeof raw !== 'object') {
            this.log.error('Invalid storage data: Not an object', raw);
        }
        if (typeof raw.nonce !== 'string' ||
            typeof raw.salt !== 'string' ||
            typeof raw.N !== 'number' ||
            typeof raw.r !== 'number' ||
            typeof raw.p !== 'number' ||
            typeof raw.encrypted !== 'string') {
            this.log.error(`Invalid storage data: One or more key is missing or invalid in ${Object.keys(raw)}`);
        }
        let d;
        try {
            d = {
                nonce: hexToU8a(raw.nonce),
                salt: hexToU8a(raw.salt),
                N: raw.N,
                r: raw.r,
                p: raw.p,
                encrypted: hexToU8a(raw.encrypted),
            };
        }
        catch (error) {
            this.log.error(`Invalid storage data`, error);
            return null;
        }
        if (d.nonce.byteLength !== nacl.secretbox.nonceLength ||
            d.salt.byteLength !== 32 ||
            d.N < this.SCRYPT_PARAMETERS.NMin ||
            d.r < this.SCRYPT_PARAMETERS.r ||
            d.p < this.SCRYPT_PARAMETERS.p ||
            d.encrypted.byteLength === 0) {
            this.log.error(`Invalid storage data: One or more value is invalid in ${Object.keys(raw)}`);
        }
        const encodedPassword = (new TextEncoder()).encode(password);
        const start = performance.now();
        const key = await scrypt(encodedPassword, d.salt, d.N, d.r, d.p, nacl.secretbox.keyLength);
        const end = performance.now();
        this.log.debug(`Used scrypt parameters (N=${d.N}, r=${d.r}, p=${d.p}) for key derivation, took ${(end - start).toFixed(0)}ms`);
        const decrypted = nacl.secretbox.open(d.encrypted, d.nonce, key);
        if (!decrypted) {
            return null;
        }
        const ownSecretKey = decrypted.slice(0, 32);
        const peerPublicKey = decrypted.slice(32, 64);
        const token = this.decodePushToken(decrypted.slice(64));
        return {
            ownSecretKey: ownSecretKey,
            peerPublicKey: peerPublicKey,
            pushToken: token.token,
            pushTokenType: token.type,
        };
    }
    clearTrustedKey() {
        this.log.debug('Clearing trusted key');
        this.storage.removeItem(this.STORAGE_KEY);
        this.storage.removeItem(this.STORAGE_KEY_AUTO_FLAG);
        this.inMemorySession.clearPassword();
    }
}
